import { useEffect, useMemo, useState } from "react"
import { useEmailTemplatePreview } from "../../hooks/useEmailTemplates"

interface Props {
  orgId: string
  type: string
  body: string
}

const PREVIEW_INERT_STYLE = `
  <style>
    a, button, input, [role="button"] {
      pointer-events: none !important;
      cursor: default !important;
    }
  </style>
`

const PREVIEW_BASE_TAG = `<base target="_blank">`

function makeInert(html: string): string {
  if (!html) return ""
  // Inject after <head> if present, otherwise prepend so it applies regardless.
  if (html.includes("<head>")) {
    return html.replace(
      "<head>",
      `<head>${PREVIEW_BASE_TAG}${PREVIEW_INERT_STYLE}`,
    )
  }
  return `${PREVIEW_BASE_TAG}${PREVIEW_INERT_STYLE}${html}`
}

export function EmailPreviewIframe({ orgId, type, body }: Props) {
  const [debouncedBody, setDebouncedBody] = useState(body)
  const preview = useEmailTemplatePreview(orgId)
  const { mutate: fetchPreview } = preview

  // Debounce body changes by 500ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedBody(body), 500)
    return () => clearTimeout(timer)
  }, [body])

  // Fetch preview whenever the debounced body changes; if body is empty let the API use the stored default
  useEffect(() => {
    if (!type) return
    fetchPreview({ type, body: debouncedBody || undefined })
  }, [debouncedBody, type, fetchPreview])

  const inertHtml = useMemo(
    () => makeInert(preview.data?.html ?? ""),
    [preview.data?.html],
  )

  return (
    <div className="relative w-full" style={{ height: 600 }}>
      {preview.isPending && (
        <div className="absolute inset-0 flex items-center justify-center bg-card/70 z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      )}
      <iframe
        srcDoc={inertHtml}
        sandbox="allow-same-origin"
        className="w-full h-full border-0"
        title="Email preview"
      />
    </div>
  )
}
